import { useEffect, useState } from "react";
import { useNode } from "@craftjs/core";
import {
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { apiClient } from "@api/index";
import { useAuthStore } from "@state/auth";

interface EventFiltersSettingsProps {
  includeCategories?: boolean;
  includeEventStatus?: boolean;
  includeKeywords?: boolean;
  includeDateRange?: boolean;
  includeSpecificEventID?: boolean;
  includeSpecificPersistentID?: boolean;
  includePersistentIDOptions?: boolean;
}

export const EventFiltersSettings: React.FC<EventFiltersSettingsProps> = ({
  includeCategories = false,
  includeEventStatus = false,
  includeKeywords = false,
  includeDateRange = false,
  includeSpecificEventID = false,
  includeSpecificPersistentID = false,
  includePersistentIDOptions = false,
}) => {
  const {
    actions: { setProp },
    categoryIDs,
    eventStatus,
    keywords,
    startDate,
    endDate,
    dateMode, // "fixed" | "relative"
    relativeAmount,
    relativeUnit, // "days" | "weeks" | "months" | "years"
    eventID,
    persistentStreams,
  } = useNode((node) => ({
    categoryIDs: node.data.props.categoryIDs,
    categoryNames: node.data.props.categoryNames,
    eventStatus: node.data.props.eventStatus,
    keywords: node.data.props.keywords,
    startDate: node.data.props.startDate,
    endDate: node.data.props.endDate,
    dateMode: node.data.props.dateMode,
    relativeAmount: node.data.props.relativeAmount,
    relativeUnit: node.data.props.relativeUnit,
    eventID: node.data.props.eventID,
    persistentStreams: node.data.props.persistentStreams,
  }));

  // ------------------------
  // LOCAL STATE
  // ------------------------
  const [localDateMode, setLocalDateMode] = useState<"fixed" | "relative">(
    dateMode || "fixed",
  );

  // ------------------------
  // DATA FETCHING
  // ------------------------
  const user = useAuthStore((state) => state.user);

  // Categories
  const { data: categories } = apiClient.category.getClientCategories.useQuery(
    { clientID: user.clientID as string },
    { refetchOnWindowFocus: false, enabled: includeCategories },
  );

  // Keywords
  const { data: keywordList } = apiClient.keyword.getKeywords.useQuery(
    { clientID: user.clientID as string },
    { refetchOnWindowFocus: false, enabled: includeKeywords },
  );

  // Persistent streams (single + multiple)
  const { data: persisStreams } =
    apiClient.event.getPersistentStreams.useQuery(
      { clientID: user.clientID as string, getThumb: true },
      {
        refetchOnWindowFocus: false,
        enabled: includeSpecificPersistentID || includePersistentIDOptions,
      },
    );

  // ------------------------
  // OPTION LISTS & DEFAULTS
  // ------------------------
  const categoryOptions =
    categories?.map((category) => ({
      title: category.displayName,
      value: category.categoryID,
      categoryName: category.categoryName,
    })) || [];

  const selectedCategories = categoryOptions.filter((option) =>
    categoryIDs?.includes(option.value),
  );

  const keywordOptions =
    keywordList?.map((keyword) => ({
      title: keyword.keyword,
      value: keyword.keywordID,
    })) || [];

  const selectedKeywords = keywordOptions.filter((option) =>
    keywords?.includes(option.title),
  );

  const persistentStreamOptions =
    persisStreams?.map((stream) => ({
      title: stream.title,
      value: stream.encoderID,
    })) || [];

  const [defaultPersistentStream, setDefaultPersistentStream] = useState<{
    title: string;
    value: string;
  } | null>(null);

  const defaultPersistentStreamsMultiple =
    persisStreams
      ?.filter(
        (stream) =>
          persistentStreams &&
          Array.isArray(persistentStreams) &&
          persistentStreams.includes(stream.encoderID),
      )
      .map((stream) => ({
        title: stream.title,
        value: stream.encoderID,
      })) || [];

  useEffect(() => {
    if (persisStreams && persistentStreams && includeSpecificPersistentID) {
      const selectedId = Array.isArray(persistentStreams)
        ? persistentStreams[0]
        : persistentStreams;
      const selectedStream = persisStreams.find(
        (stream) => stream.encoderID === selectedId,
      );
      if (selectedStream) {
        setDefaultPersistentStream({
          title: selectedStream.title,
          value: selectedStream.encoderID,
        });
      }
    }
  }, [persisStreams, persistentStreams, includeSpecificPersistentID]);

  // ------------------------
  // HANDLERS – DATE RANGE
  // ------------------------
  const handleDateModeChange = (_: any, newMode: "fixed" | "relative" | null) => {
    if (!newMode) return; // ignore null deselection
    setLocalDateMode(newMode);
    setProp((prop: any) => {
      prop.dateMode = newMode;
      if (newMode === "fixed") {
        // clear relative values
        prop.relativeAmount = null;
        prop.relativeUnit = null;
      } else {
        // clear fixed values
        prop.startDate = null;
        prop.endDate = null;
      }
    });
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((prop: any) => (prop.startDate = e.target.value ? new Date(e.target.value) : null));
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((prop: any) => (prop.endDate = e.target.value ? new Date(e.target.value) : null));
  };

  const handleRelativeAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setProp((prop: any) => (prop.relativeAmount = val ? parseInt(val, 10) : null));
  };

  const relativeUnitOptions = [
    { title: "Days", value: "days" },
    { title: "Weeks", value: "weeks" },
    { title: "Months", value: "months" },
    { title: "Years", value: "years" },
  ];

  // ------------------------
  // RENDER
  // ------------------------
  return (
    <div className="mt-2 pb-4">
      <div className="font-bold text-lg mb-1">Search Filters</div>
      <div className="flex flex-col gap-4">
        {/* ---------------- Persistent Streams (single) ---------------- */}
        {includeSpecificPersistentID && (
          <Autocomplete
            id="persistentStream"
            size="small"
            options={persistentStreamOptions}
            value={defaultPersistentStream}
            onChange={(_, newValue) => {
              setProp((props: any) => (props.persistentStreams = newValue ? [newValue.value] : []));
            }}
            getOptionLabel={(option) => option.title}
            renderInput={(params) => (
              <TextField {...params} required label="Persistent Stream" placeholder="Select a persistent stream" />
            )}
          />
        )}

        {/* ---------------- Persistent Streams (multiple) ---------------- */}
        {includePersistentIDOptions && (
          <Autocomplete
            multiple
            id="persistentStreamsMultiple"
            size="small"
            options={persistentStreamOptions}
            value={defaultPersistentStreamsMultiple}
            onChange={(_, newValue) => {
              setProp((props: any) => (props.persistentStreams = newValue.map((v) => v.value)));
            }}
            getOptionLabel={(option) => option.title}
            renderInput={(params) => <TextField {...params} label="Select 24/7 Streams" />}
          />
        )}

        {/* ---------------- Event ID ---------------- */}
        {includeSpecificEventID && (
          <TextField
            fullWidth
            size="small"
            label="Event ID"
            required
            value={eventID}
            onChange={(e) => setProp((prop: any) => (prop.eventID = e.target.value))}
          />
        )}

        {/* ---------------- Categories ---------------- */}
        {includeCategories && (
          <Autocomplete
            multiple
            id="categories"
            size="small"
            options={categoryOptions}
            value={selectedCategories}
            onChange={(_, newValue) => {
              setProp((props: any) => {
                props.categoryIDs = newValue.map((v) => v.value);
                props.categoryNames = newValue.map((v) => v.categoryName);
              });
            }}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label="Categories" placeholder="Select categories" />}
          />
        )}

        {/* ---------------- Event Status ---------------- */}
        {includeEventStatus && (
          <Autocomplete
            id="eventStatus"
            size="small"
            options={[
              { title: "Live", value: "live" },
              { title: "Published", value: "published" },
              { title: "New", value: "new" },
            ]}
            value={
              [
                { title: "Live", value: "live" },
                { title: "Published", value: "published" },
                { title: "New", value: "new" },
              ].find((status) => status.value === eventStatus) || null
            }
            onChange={(_, newValue) => {
              setProp((props: any) => (props.eventStatus = newValue?.value || null));
            }}
            getOptionLabel={(option) => option.title}
            renderInput={(params) => <TextField {...params} label="Event Status" />}
          />
        )}

        {/* ---------------- Keywords ---------------- */}
        {includeKeywords && (
          <Autocomplete
            multiple
            size="small"
            id="keywords"
            options={keywordOptions}
            value={selectedKeywords}
            onChange={(_, newValue) => {
              setProp((props: any) => (props.keywords = newValue.map((v) => v.title)));
            }}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Select keywords" />}
          />
        )}

        {/* ---------------- Date Range ---------------- */}
        {includeDateRange && eventStatus !== "live" && (
          <>
            {/* Toggle */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={localDateMode}
              onChange={handleDateModeChange}
            >
              <ToggleButton value="fixed">Fixed</ToggleButton>
              <ToggleButton value="relative">Relative</ToggleButton>
            </ToggleButtonGroup>

            {/* Fixed mode */}
            {localDateMode === "fixed" && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate ? new Date(startDate).toISOString().split("T")[0] : ""}
                  onChange={handleStartDateChange}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate ? new Date(endDate).toISOString().split("T")[0] : ""}
                  onChange={handleEndDateChange}
                />
              </>
            )}

            {/* Relative mode */}
            {localDateMode === "relative" && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={relativeAmount ?? ""}
                  onChange={handleRelativeAmountChange}
                />
                <Autocomplete
                  id="relative-unit"
                  size="small"
                  options={relativeUnitOptions}
                  value={
                    relativeUnitOptions.find((u) => u.value === relativeUnit) || null
                  }
                  onChange={(_, newValue) =>
                    setProp((prop: any) => (prop.relativeUnit = newValue?.value || null))
                  }
                  getOptionLabel={(option) => option.title}
                  renderInput={(params) => <TextField {...params} label="Unit" />}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
